<script setup lang="ts">
/**
 * Релизы — макет 92:1066: слева история релизов, справа форма создания.
 *
 * История приходит из GitLab, а не из журнала операций трекера (DEV-1594): в
 * журнале видны только релизы, собранные его же кнопкой, а тег, поставленный
 * руками, в историю не попадал вовсе — раздел врал про то, что выкачено. В
 * этом проекте релиз это семверный тег на master, поэтому читаются теги.
 *
 * Группировка по репозиториям — второй режим списка: «что выкатывали недавно»
 * и «что выкатывали в этом сервисе» — разные вопросы, и оба задают часто.
 *
 * Блока «версии сервисов сейчас» из макета здесь нет: трекер не знает, какая
 * версия каждого сервиса стоит на dev и на prod — ни ручки, ни зеркала версий
 * у него нет (docs/design-gaps.md).
 *
 * Чек-лист готовности из макета — по той же причине частичный: «пайплайн master
 * зелёный» и «все задачи готовы» считать нечем.
 */
import { AppButton, AppModal, AppSegmentedControl, AppTextField } from '@vinprop/design-system'
import { AlertTriangle, Search, Tag } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import FilterChip from '@/components/FilterChip.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { useAuthStore } from '@/features/auth/store'
import {
  groupByRepository,
  listReleases,
  listRepositoryPaths,
  sortReleases,
  type Release,
  type ReleaseOrder,
} from '@/features/devops/gitlab'
import { deployRelease } from '@/features/devops/api'
import ReleaseForm from '@/features/devops/components/ReleaseForm.vue'
import ReleaseRow from '@/features/devops/components/ReleaseRow.vue'
import { formatStamp } from '@/features/issues/format'
import { errorMessage } from '@/api/errors'

/** Окна периода из макета: «за 30 дней» и всё остальное. */
const PERIODS = [
  { value: '30', days: 30 },
  { value: '90', days: 90 },
]

const { t, locale } = useI18n()
const auth = useAuthStore()

/**
 * Разрезы по сборке: два состояния, ради которых на колонку и смотрят, плюс
 * «без сборки» — старый тег, чей прогон уже вычищен, или выкат, который так и
 * не запускали.
 */
const BUILD_FILTERS = ['success', 'failed', 'running', 'none'] as const

const releases = ref<Release[]>([])
const isLoading = ref(false)
const error = ref('')
const search = ref('')
const serviceFilter = ref<string[]>([])
const periodFilter = ref<string[]>([])
const buildFilter = ref<string[]>([])
const order = ref<ReleaseOrder>('newest')
const view = ref<'flat' | 'grouped'>('flat')
const form = ref<InstanceType<typeof ReleaseForm> | null>(null)

const grouped = computed(() => view.value === 'grouped')

const views = computed(() => [
  { value: 'flat' as const, label: t('devops.releases.views.flat') },
  { value: 'grouped' as const, label: t('devops.releases.views.grouped') },
])

const allowed = computed(() => auth.can('tracker:devops'))

const serviceOptions = computed(() =>
  [...new Set(releases.value.map((release) => release.projectPath))].map((path) => ({
    value: path,
    label: path,
  })),
)

const periodOptions = computed(() =>
  PERIODS.map((period) => ({
    value: period.value,
    label: t('devops.releases.filters.days', { days: period.days }),
  })),
)

const buildOptions = computed(() =>
  BUILD_FILTERS.map((value) => ({
    value,
    label: t(`devops.releases.filters.build.${value}`),
  })),
)

const orderOptions = computed(() => [
  { value: 'newest', label: t('devops.releases.order.newest') },
  { value: 'oldest', label: t('devops.releases.order.oldest') },
])

/**
 * Подсказка формы — подключённые репозитории, а не те, что встретились в
 * истории: собрать релиз нужно и для сервиса, который ни разу не выкатывали
 * через трекер (DEV-1594).
 */
const knownServices = ref<string[]>([])

const visible = computed(() => {
  const needle = search.value.trim().toLowerCase()
  const days = periodFilter.value.length
    ? Math.max(...periodFilter.value.map((value) => Number(value)))
    : 0
  const since = days ? Date.now() - days * 86_400_000 : 0

  const found = releases.value.filter((release) => {
    if (serviceFilter.value.length && !serviceFilter.value.includes(release.projectPath)) {
      return false
    }

    if (buildFilter.value.length && !buildFilter.value.includes(release.pipeline?.status ?? 'none')) {
      return false
    }

    if (since && new Date(release.createdAt).getTime() < since) return false

    return !needle || `${release.projectPath} ${release.tag}`.toLowerCase().includes(needle)
  })

  return sortReleases(found, order.value)
})

/** Группы для второго режима списка; в плоском режиме не считаются */
const groups = computed(() => (grouped.value ? groupByRepository(visible.value) : []))

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    releases.value = await listReleases()
  } catch {
    error.value = t('devops.releases.error')
  } finally {
    isLoading.value = false
  }

  // Список сервисов формы приезжает своим запросом: история его не заменяет,
  // а отказ по нему не повод прятать историю.
  try {
    knownServices.value = await listRepositoryPaths()
  } catch {
    knownServices.value = []
  }
}

const stamp = (iso: string) =>
  formatStamp(iso, locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  })

/** Релиз в списке опознаётся парой «репозиторий + тег»: идентификатора у него нет */
const key = (release: Release) => `${release.projectPath}-${release.tag}`

/** Релиз, чей выкат подтверждают; пусто — диалога нет */
const pending = ref<Release | null>(null)
/** Ключ релиза, выкат которого сейчас запрашивается */
const deployingKey = ref('')
const deployError = ref('')

const askDeploy = (release: Release) => {
  deployError.value = ''
  pending.value = release
}

/**
 * Подтверждение обязательно: это единственное действие раздела, которое
 * меняет прод, и отменить его из трекера нельзя — джоба уже уехала в GitLab.
 */
const confirmDeploy = async () => {
  const release = pending.value

  if (!release) return

  deployingKey.value = key(release)
  deployError.value = ''

  try {
    await deployRelease(release.projectPath, release.tag)
    pending.value = null
    await load()
  } catch (failure) {
    // Отказ показываем в диалоге, а не тостом: тут же написано, что именно
    // не поехало, и диалог остаётся открытым для второй попытки. Сервис
    // отвечает по-человечески — «джоба не ждёт запуска», «нет пайплайна».
    deployError.value = errorMessage(failure, t('devops.releases.deploy.failed'))
  } finally {
    deployingKey.value = ''
  }
}

/**
 * Пока выкат идёт, история сама себя обновляет: статус прогона приезжает
 * вебхуком, и без опроса строка так и осталась бы «ждёт выката» до тех пор,
 * пока экран не перезагрузят руками. Таймер живёт только вместе с живым
 * прогоном — на устоявшемся списке опроса нет.
 */
const REFRESH_MS = 15_000
let timer: ReturnType<typeof setInterval> | undefined

const isLive = (release: Release) =>
  ['running', 'pending', 'created'].includes(release.pipeline?.status ?? '')

const syncTimer = () => {
  const live = releases.value.some(isLive)

  if (live && !timer) {
    timer = setInterval(() => void load(), REFRESH_MS)
  } else if (!live && timer) {
    clearInterval(timer)
    timer = undefined
  }
}

watch(releases, syncTimer)
onBeforeUnmount(() => clearInterval(timer))

onMounted(load)
</script>

<template>
  <TrackerToolbar
    :breadcrumbs="[
      { label: t('nav.devops'), to: '/devops' },
      { label: t('devops.releases.title') },
    ]"
  >
    <FilterChip
      v-model="serviceFilter"
      :label="t('devops.releases.filters.service')"
      :options="serviceOptions"
      :empty-text="t('devops.releases.filters.empty')"
    />
    <FilterChip
      v-model="periodFilter"
      :label="t('devops.releases.filters.period')"
      :options="periodOptions"
      :empty-text="t('devops.releases.filters.empty')"
    />

    <FilterChip
      v-model="buildFilter"
      :label="t('devops.releases.filters.buildLabel')"
      :options="buildOptions"
      :empty-text="t('devops.releases.filters.empty')"
    />

    <!-- Порядок — выбор из двух, поэтому чип отдаёт последнее нажатое, а не
         набор: «сразу и новые сверху, и старые» смысла не имеет. -->
    <FilterChip
      :model-value="[order]"
      :label="t('devops.releases.order.label')"
      :options="orderOptions"
      :empty-text="t('devops.releases.filters.empty')"
      @update:model-value="order = ($event[$event.length - 1] as ReleaseOrder) ?? 'newest'"
    />

    <AppSegmentedControl
      v-model="view"
      :options="views"
      size="small"
      :aria-label="t('devops.releases.views.label')"
    />

    <div class="releases__search">
      <AppTextField
        v-model="search"
        size="small"
        filled
        :placeholder="t('devops.releases.searchPlaceholder')"
        :prefix-icon="Search"
        :aria-label="t('devops.releases.searchPlaceholder')"
      />
    </div>

    <!-- Форма стоит на экране, кнопка панели просто уводит к ней курсором -->
    <AppButton v-if="allowed" variant="primary" size="small" @click="form?.focus()">
      {{ t('devops.release.create') }}
    </AppButton>
  </TrackerToolbar>

  <div class="releases">
    <div class="releases__main">
    <h2 class="releases__overline">{{ t('devops.releases.history') }}</h2>

    <div v-if="isLoading && !releases.length" class="releases__skeleton" />

    <TrackerEmptyState
      v-else-if="error"
      :icon="AlertTriangle"
      :title="t('devops.releases.error')"
      :description="t('devops.releases.errorHint')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('devops.envs.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!visible.length"
      :icon="Tag"
      :title="t('devops.releases.empty.title')"
      :description="t('devops.releases.empty.description')"
    />

    <div v-else class="releases__table">
      <template v-if="grouped">
        <template v-for="group in groups" :key="group.path">
          <div class="releases__group">
            <span class="releases__group-path">{{ group.path }}</span>
            <span class="releases__group-count">
              {{ t('devops.releases.inRepository', group.releases.length) }}
            </span>
          </div>

          <ReleaseRow
            v-for="release in group.releases"
            :key="`${release.projectPath}-${release.tag}`"
            :release="release"
            :stamp="stamp(release.createdAt)"
            :can-deploy="allowed"
            :is-deploying="deployingKey === key(release)"
            @deploy="askDeploy"
          />
        </template>
      </template>

      <ReleaseRow
        v-for="release in grouped ? [] : visible"
        :key="`${release.projectPath}-${release.tag}`"
        :release="release"
        :stamp="stamp(release.createdAt)"
        with-service
        :can-deploy="allowed"
        :is-deploying="deployingKey === key(release)"
        @deploy="askDeploy"
      />
    </div>

      <p class="releases__note">{{ t('devops.releases.note') }}</p>
    </div>

    <!-- Диалог выката — макет 286:2182. Единственное действие раздела, которое
         меняет прод, поэтому спрашивает подтверждение, а не срабатывает сразу. -->
    <AppModal
      :model-value="Boolean(pending)"
      :title="t('devops.releases.deploy.confirmTitle', { tag: pending?.tag ?? '' })"
      size="small"
      @update:model-value="pending = null"
    >
      <p class="releases__confirm-text">{{ t('devops.releases.deploy.confirmBody') }}</p>

      <div class="releases__confirm-meta">
        <span>{{ pending?.projectPath }}</span>
        <span v-if="pending?.pipeline">
          {{ t('devops.releases.deploy.pipeline', { id: pending.pipeline.id }) }}
        </span>
      </div>

      <p v-if="deployError" class="releases__confirm-error">{{ deployError }}</p>

      <template #footer>
        <AppButton variant="secondary" @click="pending = null">
          {{ t('common.actions.cancel') }}
        </AppButton>
        <AppButton
          variant="primary"
          :loading="Boolean(deployingKey)"
          @click="confirmDeploy"
        >
          {{ t('devops.releases.deploy.submit') }}
        </AppButton>
      </template>
    </AppModal>

    <!-- Правая колонка макета (92:1398): форма создания стоит на экране, а не
         прячется в модалку — релиз собирают, глядя на историю рядом. -->
    <aside class="releases__side">
      <h2 class="releases__overline">{{ t('devops.release.title') }}</h2>
      <div class="releases__form">
        <ReleaseForm ref="form" :services="knownServices" @done="load" />
      </div>
    </aside>
  </div>
</template>

<style scoped>
/* Прокручивается только история: форма сбора релиза стоит на месте, иначе за
   длинным списком её приходится искать прокруткой вверх — а собирают релиз,
   как раз глядя на историю. */
.releases {
  display: flex;
  flex: 1;
  align-items: flex-start;
  gap: 24px;
  min-height: 0;
  padding: 28px 48px;
  overflow: hidden;
}

/* Две колонки макета: история 740 и форма 340. На узком экране колонки
   встают друг под друга — форма и без соседа остаётся рабочей. */
.releases__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  max-height: 100%;
  overflow-y: auto;
}

.releases__side {
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 340px;
  flex-shrink: 0;
  max-height: 100%;
  overflow-y: auto;
}

.releases__form {
  padding: 18px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
}

@media (max-width: 1100px) {
  .releases {
    flex-direction: column;
  }

  .releases__side {
    width: 100%;
  }
}

.releases__issue {
  color: var(--color-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.releases__issue:hover {
  text-decoration: underline;
}

.releases__overline {
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.releases__skeleton {
  height: 240px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.releases__table {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

/* Заголовок группы: репозиторий и сколько релизов внутри */
.releases__group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-muted);
}

.releases__group-path {
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
}

.releases__group-count {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.releases__badge {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
}

.releases__badge--done {
  background: var(--color-success-tint);
  color: var(--color-success-strong);
}

.releases__badge--failed {
  background: var(--color-danger-tint);
  color: var(--color-danger-strong);
}

.releases__confirm-text {
  margin: 0 0 12px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
}

/* Что именно поедет: путь репозитория и номер прогона. Тег назван в заголовке
   диалога, поэтому здесь его нет — повтор только удлинял бы чтение. */
.releases__confirm-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
}

.releases__confirm-error {
  margin: 12px 0 0;
  color: var(--color-danger-strong);
  font-size: var(--font-size-body-sm);
}

.releases__note {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.releases__search {
  width: 220px;
}
</style>
